import {Route, buildRoute} from '@synako/enhanced-router';
import {ClientApp} from 'api-types/ffconnect';
import {routes} from 'ff-common';
import {matchPath} from 'react-router-dom';

/**
 * Mode applicatif courant.
 *
 * Les trois modes correspondent à des contextes d'utilisation distincts :
 *
 * - 'client'     : client final sur /ffconnect/ (pas de routing unifié)
 * - 'commercial' : utilisateur BO qui commande pour le compte du client (peut valider le panier)
 * - 'assistance' : utilisateur BO en lecture seule / aide (liens d'action masqués côté backend via permissions)
 *
 * Le mode assistance est déterminé par la query string `?assistance=1` dans l'URL.
 * Sans ce paramètre, sur /ffconnect/c/:client_id/ → commercial.
 * Sur /ffconnect/ (pas de routing unifié) → client.
 *
 * Ce mode est lu au démarrage dans main.tsx pour initialiser le header HTTP x-client-app
 * envoyé par halitrek à chaque requête API.
 */
export type AppMode = 'client' | 'commercial' | 'assistance';

export const UNIFIED_ROUTING_PATTERN = '/ffconnect/c/:client_id/*';

/**
 * Détermine le mode applicatif depuis l'URL courante.
 */
export function getModeFromUrl(): AppMode {
  if (!matchPath(UNIFIED_ROUTING_PATTERN, window.location.pathname))
    return 'client';
  const params = new URLSearchParams(window.location.search);
  return params.get('assistance') === '1' ? 'assistance' : 'commercial';
}

/**
 * Ajoute à une URI d'API le paramètre `client_app` de l'application courante :
 * `backoffice` sous le back-office, sinon le mode donné par `getModeFromUrl()`.
 *
 * À utiliser pour toute requête que le navigateur émet lui-même (`<img src>`, lien,
 * `window.open`) : elle part sans le header `x-client-app` que halitrek pose sur ses
 * propres appels, et le serveur ne lit alors que la session client. Un commercial
 * connecté au seul back-office (Microsoft) y est traité en visiteur, d'où un 403 ;
 * un compte Google Synako ouvre les deux sessions et ne voit pas le défaut.
 * halirun lit le paramètre comme le header, puis le retire de la query avant la validation.
 */
export function withClientApp(uri: string): string {
  const url = new URL(uri, window.location.origin);
  const isBackOffice = matchPath(
    `${routes.client.backOffice.path}/*`,
    window.location.pathname,
  );
  // Typé par l'enum du serveur
  const clientApp: ClientApp = isBackOffice ? 'backoffice' : getModeFromUrl();
  url.searchParams.set('client_app', clientApp);
  return url.toString();
}

export interface ModeTheme {
  isAssistance: boolean;
  /** Renvoie le token `assistance` (rouge) en mode assistance, sinon le token `defaut` inchangé. */
  modeColor: (defaut: string, assistance: string) => string;
}

/**
 * Thème par mode applicatif (Prise de commande V3).
 *
 * En mode assistance => theme "rouge"
 * En mode commercial / client => on conserve thème bleu.
 *
 * Lu au render comme `getModeFromUrl()` — le mode ne change pas sans rechargement.
 */
export function getModeTheme(): ModeTheme {
  const isAssistance = getModeFromUrl() === 'assistance';
  return {
    isAssistance,
    modeColor: (defaut, assistance) => (isAssistance ? assistance : defaut),
  };
}

/**
 * Construit l'URL de bascule de mode (commercial ↔ assistance) à partir de l'URL
 * courante : ajoute ou retire `?assistance=1` en conservant le path et les autres
 * paramètres. La navigation se fait via `window.location.href` (rechargement complet)
 * pour réinitialiser le header `x-client-app` envoyé par halitrek.
 */
export function buildSwitchModeUrl(toAssistance: boolean): string {
  // Clone l'URL courante (path + query) pour ne basculer que le paramètre `assistance`
  const url = new URL(window.location.href);
  if (toAssistance) {
    url.searchParams.set('assistance', '1');
  } else {
    url.searchParams.delete('assistance');
  }
  return url.toString();
}

/**
 * Construit une URL du mode commercial pour un `client_id` donné depuis une route
 * client typée. Utilisé depuis le BO (fiche client, etc.) pour générer un lien
 * `/ffconnect/c/{client_id}/...` à partir d'une route client (ex: `routes.client.cadencier`).
 *
 * Hors d'un composant React (pas un hook) — différent de `useClientRoute` qui s'appuie
 * sur le `client_id` extrait de l'URL courante. Ici on passe le `client_id` explicitement.
 */

export function buildModeCommercialUrl(
  client_id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  route: Route<any>,
  routeParams?: Record<string, string>,
  query?: Record<string, string | string[] | undefined>,
): string {
  const basePath = buildRoute(route, routeParams, query);
  return basePath.replace('/ffconnect', `/ffconnect/c/${client_id}`);
}
